// src/components/offcanvas/MeasureOffcanvas.js

import React, { useState, useEffect } from 'react';
import {
    Offcanvas,
    Form,
    Dropdown,
    Row,
    Col,
    OverlayTrigger,
    Tooltip,
    Badge,
    Button,
    Modal,
    Toast,
    ToastContainer,
    ListGroup
} from 'react-bootstrap';
import ExternalLinkIcon from '../../assets/gptw_icons/Icons_rot/link-external-01.png';
import TextareaAutosize from 'react-textarea-autosize';
import { useDispatch, useSelector } from 'react-redux';
import { saveMeasure, createMeasure, deleteMeasure } from '../../features/measuresSlice';
import { selectIsUserLeaderOfUnitById } from '../../features/unitsSlice';
import { fetchCheckpoints } from '../../features/checkpointsSlice';
import MeasureCheckpointList from '../MeasureCheckpointList';


import CustomOffcanvas from './CustomOffcanvas';


import AddCalendarIcon from '../../assets/gptw_icons/Icons_rot/calendar-check-02.png';
import AddResponsibleIcon from '../../assets/gptw_icons/Icons_rot/user-edit.png';
import AddLoadingIcon from '../../assets/gptw_icons/Icons_rot/loading-01.png';
import InfoSquareIcon from '../../assets/gptw_icons/Icons_rot/info-square.png';
import TrashIcon from '../../assets/gptw_icons/Icons_rot/trash-01.png';
import { IconLock } from '@tabler/icons-react';

import ActionAreaOffcanvas from './ActionAreaOffcanvas';
import './OffcanvasResponsive.css';

const MeasureOffcanvas = ({
    showOffcanvas,
    handleCloseOffcanvas,
    measureId,
    actionAreaId,
    unitName,
    unitId: propUnitId, // Accept unitId as a prop for fallback
}) => {
    const dispatch = useDispatch();

    // Fetch measure data from Redux store
    const measureData = useSelector((state) => state.measures.entities[measureId]);
    // Retrieve the action area data from the Redux store
    const actionAreaData = useSelector((state) => state.actionAreas.entities[actionAreaId]);
    // Determine the unit ID - use actionAreaData.unit if available, otherwise use the prop
    const effectiveUnitId = actionAreaData?.unit || propUnitId;
    // Check if user is leader of the current unit
    const isUnitLeader = useSelector((state) => selectIsUserLeaderOfUnitById(state, effectiveUnitId));
    // Get checkpoints status to know if we need to fetch
    const checkpointsStatus = useSelector((state) => state.checkpoints.status);

    // Fetch checkpoints if not already loaded (needed for MeasureCheckpointList)
    useEffect(() => {
        if (checkpointsStatus === 'idle') {
            dispatch(fetchCheckpoints());
        }
    }, [dispatch, checkpointsStatus]);

    // Define initial form values for the measure
    const initialFormValues = {
        id: measureId || null,
        measure_title: '',
        responsible_person: '',
        ends_at: '',
        progress: '',
        description: '',
        actionarea: actionAreaId,
    };

    // Local state for form values
    const [formValues, setFormValues] = useState(() => {
        return measureData ? { ...initialFormValues, ...measureData } : initialFormValues;
    });
    const [isDirty, setIsDirty] = useState(false);

    // Update form values when measureData changes
    useEffect(() => {
        if (measureData) {
            setFormValues({ ...initialFormValues, ...measureData });
            setIsDirty(false);
        }
    }, [measureData]);

    const handleInputChange = (e) => {
        // Only process changes if user is a unit leader
        if (!isUnitLeader) return;
        
        const { name, value } = e.target;
        setFormValues((prevValues) => ({ ...prevValues, [name]: value }));
        setIsDirty(true);
    };

    const handleStatusChange = (status) => {
        // Only process changes if user is a unit leader
        if (!isUnitLeader) return;
        
        setFormValues((prevValues) => ({ ...prevValues, progress: status }));
        setIsDirty(true);
    };

    const handleOffcanvasHide = () => {
        if (isDirty && isUnitLeader) {
            if (measureId) {
                // Save an existing measure
                dispatch(saveMeasure({ actionAreaId, measure: formValues }));
            } else {
                // Create a new measure
                dispatch(createMeasure({ actionAreaId, measure: formValues }));
            }
        }
        handleCloseOffcanvas();
    };

    // Optional: Loading and error states for saving/creating
    const saveStatus = useSelector((state) => state.measures.saveStatus);
    const saveError = useSelector((state) => state.measures.saveError);
    const createStatus = useSelector((state) => state.measures.createStatus);
    const createError = useSelector((state) => state.measures.createError);

    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastVariant, setToastVariant] = useState('success');

    // Handle delete confirmation
    const handleDeleteClick = () => {
        if (!isUnitLeader) return;
        setShowConfirmModal(true);
    };

    const handleConfirmDelete = async () => {
        if (!isUnitLeader) return;
        
        try {
            await dispatch(deleteMeasure({ actionAreaId, measureId })).unwrap();
            setToastMessage('Measure deleted successfully.');
            setToastVariant('success');
            setShowToast(true);
            handleCloseOffcanvas(); // Close the offcanvas
        } catch (error) {
            setToastMessage(`Failed to delete measure: ${error}`);
            setToastVariant('danger');
            setShowToast(true);
        } finally {
            setShowConfirmModal(false);
        }
    };

    const handleCancelDelete = () => {
        setShowConfirmModal(false);
    };

    // Determine if this measure exists (for showing the Delete button)
    const isExistingMeasure = !!measureId;

    const [showActionAreaOffcanvas, setShowActionAreaOffcanvas] = useState(false);

    return (
        <>
            <CustomOffcanvas
                show={showOffcanvas}
                onHide={handleOffcanvasHide}
                placement="end"
                className="responsive-offcanvas"
            >
                <Offcanvas.Header closeButton style={{ paddingBottom: '0px' }}>
                    <div className="w-100">
                        <Row className="mb-3">
                            <Col className="d-flex justify-content-between align-items-center">
                                {isExistingMeasure && isUnitLeader && (
                                    <OverlayTrigger
                                        placement="right"
                                        overlay={<Tooltip id="delete-tooltip">Maßnahme löschen?</Tooltip>}
                                    >
                                        <Button
                                            variant="link"
                                            onClick={handleDeleteClick}
                                            className="p-0"
                                        >
                                            <img
                                                src={TrashIcon}
                                                alt="Delete"
                                                style={{
                                                    width: '24px',
                                                    height: '24px'
                                                }}
                                            />
                                        </Button>
                                    </OverlayTrigger>
                                )}
                                
                                {/* Read-only indicator - only show if user is not a unit leader */}
                                {!isUnitLeader && (
                                    <div className="d-flex align-items-center">
                                        <IconLock size={18} stroke={1.5} className="text-secondary" />
                                        <span className="ms-2 text-secondary">Nur Leserechte</span>
                                    </div>
                                )}
                                {unitName && (
                                    <Badge bg="secondary" className="ms-auto">
                                        {unitName}
                                    </Badge>
                                )}
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <Form.Label className="mb-0 text-muted fw-bold">Titel der Maßnahme</Form.Label>
                                <TextareaAutosize
                                    minRows={1}
                                    className="form-control"
                                    name="measure_title"
                                    style={{
                                        border: 'none',
                                        outline: 'none',
                                        boxShadow: 'none',
                                        backgroundColor: 'transparent',
                                        resize: 'none',
                                        fontSize: '2rem',
                                        fontWeight: 'bold',
                                        paddingBottom: '0',
                                        paddingTop: '0',
                                        cursor: isUnitLeader ? 'text' : 'default',
                                    }}
                                    value={formValues.measure_title}
                                    onChange={handleInputChange}
                                    placeholder="Titel der Maßnahme"
                                    readOnly={!isUnitLeader}
                                />
                            </Col>
                        </Row>
                    </div>
                </Offcanvas.Header>

                <Offcanvas.Body style={{ paddingTop: '0px' }}>
                    <hr className="mt-0" />
                    <Form>
                        <Form.Group controlId="formDescription" className="mt-3">
                            <Form.Label className="d-flex align-items-center">
                                Beschreibung der Maßnahme
                                <OverlayTrigger
                                    placement="right"
                                    overlay={
                                        <Tooltip id="beschreibung-tooltip">
                                            Bitte gib eine detaillierte Beschreibung der Maßnahme ein.
                                        </Tooltip>
                                    }
                                >
                                    <img
                                        src={InfoSquareIcon}
                                        alt="Info"
                                        style={{ marginLeft: '8px', cursor: 'pointer', width: '20px', height: '20px' }}
                                    />
                                </OverlayTrigger>
                            </Form.Label>
                            <TextareaAutosize
                                minRows={4}
                                className="form-control mb-3"
                                name="description"
                                value={formValues.description || ''}
                                onChange={handleInputChange}
                                placeholder="Beschreibe konkret die Maßnahme. Was sind die einzelnen Schritte in der Umsetzung?"
                                readOnly={!isUnitLeader}
                                style={{
                                    backgroundColor: isUnitLeader ? '#fff' : '#f8f9fa',
                                    cursor: isUnitLeader ? 'text' : 'default',
                                }}
                            />
                        </Form.Group>

                        <Form.Group controlId="formWhoIsResponsible" className="mb-3">
                            <Row>
                                <Col xs={12} md={4} className="d-flex align-items-center">
                                    <Form.Label>
                                        <img className="label-icon" src={AddResponsibleIcon} alt="Responsible Icon" />
                                        Wer ist verantwortlich?
                                    </Form.Label>
                                </Col>
                                <Col xs={12} md={8}>
                                    <Form.Control
                                        value={formValues.responsible_person || ''}
                                        name="responsible_person"
                                        onChange={handleInputChange}
                                        style={{ 
                                            width: '100%',
                                            backgroundColor: isUnitLeader ? '#fff' : '#f8f9fa',
                                            cursor: isUnitLeader ? 'text' : 'default',
                                        }}
                                        placeholder="Name der verantwortlichen Person"
                                        readOnly={!isUnitLeader}
                                    />
                                </Col>
                            </Row>
                        </Form.Group>

                        <Form.Group controlId="formUntilWhen" className="mb-3">
                            <Row>
                                <Col xs={12} md={4} className="d-flex align-items-center">
                                    <Form.Label>
                                        <img className="label-icon" src={AddCalendarIcon} alt="Calendar Icon" />
                                        Bis wann erfolgt die Umsetzung?
                                    </Form.Label>
                                </Col>
                                <Col xs={12} md={8}>
                                    <Form.Control
                                        type="date"
                                        name="ends_at"
                                        style={{ 
                                            width: '100%',
                                            backgroundColor: isUnitLeader ? '#fff' : '#f8f9fa',
                                            cursor: isUnitLeader ? 'text' : 'default',
                                        }}
                                        value={formValues.ends_at || ''}
                                        onChange={handleInputChange}
                                        readOnly={!isUnitLeader}
                                    />
                                </Col>
                            </Row>
                        </Form.Group>
                    </Form>

                    {measureId && (
                        <>
                            <hr />
                            <h5 className="fw-bold mb-3">Checkpoints für diese Maßnahme</h5>
                            <MeasureCheckpointList
                                measureId={measureId}
                                fallbackUnitId={effectiveUnitId}
                                onCheckpointNavigate={handleCloseOffcanvas}
                            />
                        </>
                    )}

                    {actionAreaData && (
                        <>
                            <hr />
                            <div
                                onClick={() => setShowActionAreaOffcanvas(true)}
                                style={{ cursor: 'pointer' }}
                            >
                                <h5 className="fw-bold">Handlungsfeld</h5>
                                <ListGroup variant="flush">
                                    <ListGroup.Item>
                                        <div className="d-flex align-items-center">
                                            <img
                                                src={ExternalLinkIcon}
                                                alt="Open"
                                                style={{
                                                    width: '16px',
                                                    height: '16px',
                                                    marginRight: '8px'
                                                }}
                                            />
                                            <div>
                                                <strong>{actionAreaData.actionarea_title || 'Kein Titel'}</strong>
                                                {actionAreaData.description && (
                                                    <div className="text-muted">{actionAreaData.description}</div>
                                                )}
                                            </div>
                                        </div>
                                    </ListGroup.Item>
                                </ListGroup>
                            </div>
                        </>
                    )}
                </Offcanvas.Body>
            </CustomOffcanvas>

            {showActionAreaOffcanvas && (
                <ActionAreaOffcanvas
                    showOffcanvas={true}
                    handleCloseOffcanvas={() => setShowActionAreaOffcanvas(false)}
                    actionAreaData={actionAreaData}
                    isUnitLeader={isUnitLeader}
                    unitName={unitName}
                    // A simple stub for handling data changes; you may replace this with a real handler if needed.
                    handleActionAreaDataChange={(newData) => console.log('Update Action Area:', newData)}
                />
            )}

            <Modal show={showConfirmModal} onHide={handleCancelDelete} centered>
                <Modal.Header closeButton>
                    <Modal.Title className="fw-bold">Löschvorgang bestätigen</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Möchten du diese Maßnahme wirklich löschen?
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCancelDelete}>
                        Abbrechen
                    </Button>
                    <Button onClick={handleConfirmDelete}>
                        Löschen
                    </Button>
                </Modal.Footer>
            </Modal>

            <ToastContainer position="bottom-center" className="p-3">
                <Toast
                    onClose={() => setShowToast(false)}
                    show={showToast}
                    delay={3000}
                    autohide
                    bg={toastVariant}
                >
                    <Toast.Header>
                        <strong className="me-auto">
                            {toastVariant === 'success' ? 'Erfolg' : 'Fehler'}
                        </strong>
                    </Toast.Header>
                    <Toast.Body className="text-white">{toastMessage}</Toast.Body>
                </Toast>
            </ToastContainer>
        </>
    );
};

export default MeasureOffcanvas;
